import { useLangContext } from '../../providers/LangProvider/LangProviderContext';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { postRequest } from '../../app/request';
import { usePopupContext } from '../Popup/PopupContext';
import { getAppPartner } from '../Auth/authSelectors';
import { PARTNER_OFFERS_STATUS } from '../Partner/partnerConstants';
import {
    OfferInfoConditionsStyled,
    OfferInfoContentHeaderStyled,
    OfferInfoContentStyled,
    OfferInfoContentTitleContainerStyled,
    OfferInfoControlPanelStyled,
    OfferInfoReferralLinkStyled,
    OfferInfoStatusIndicatorStyled,
    OfferInfoStatusStyled,
    OfferInfoStyled,
    OfferInfoTypeStyled,
} from './styled/OfferInfoStyled';
import classnames from 'classnames';
import amountFormat from '../../utils/amountFormat';
import { formatTurnoverLots } from '../../utils/turnoverToLots.js';
import { Button } from 'react-bootstrap';
import Campaigns from '../Campaigns/Campaigns';
import OfferReferralLink from './OfferReferralLink';
import PartnerPostbacks from '../PartnerPostbacks/PartnerPostbacks';

function OfferInfo(props) {
    const { offerInfo, getOffersList } = props;
    const { getLangPath } = useLangContext();
    const { catchPopup } = usePopupContext();
    const partnerInfo = useSelector(getAppPartner);
    const ibOverridePercent = Number(partnerInfo?.ib_override_percent) || 0;
    const hasInout = Number(offerInfo.inout_percent) > 0;
    const hasRevshare = Number(offerInfo.revshare_percent) > 0;
    const isTypeCustom = !offerInfo.ftd && !offerInfo.turnover && !hasInout && !hasRevshare;

    const isOfferActive = offerInfo.status === PARTNER_OFFERS_STATUS.ACTIVE;
    const isOfferWaiting = offerInfo.status === PARTNER_OFFERS_STATUS.WAITING;

    const currentType = useMemo(() => ([
        offerInfo.ftd ? 'CPA' : undefined,
        offerInfo.turnover ? 'IB' : undefined,
        hasInout ? 'InOut' : undefined,
        hasRevshare ? 'Revshare' : undefined,
        isTypeCustom ? 'Custom' : undefined,
    ].filter(Boolean).join(' ')), [offerInfo, hasInout, hasRevshare, isTypeCustom]);

    const handleApplyClick = useCallback(() => {
        postRequest(`/partner/offers/${offerInfo.offer_id}/apply`, {})
            .then(() => getOffersList())
            .catch(e => {
                catchPopup(e.response);
            });
    }, [offerInfo, catchPopup, getOffersList]);

    const handleDeclineClick = useCallback(() => {
        const isConfirmed = window.confirm(getLangPath('offers.decline_confirm'));
        if (isConfirmed) {
            postRequest(`/partner/offers/${offerInfo.offer_id}/decline`, {})
                .then(() => getOffersList())
                .catch((e) => {
                    catchPopup(e.response);
                });
        }
    }, [catchPopup, offerInfo, getOffersList, getLangPath]);

    return <OfferInfoStyled>
        <OfferInfoContentStyled>
            <OfferInfoContentHeaderStyled>
                <OfferInfoContentTitleContainerStyled>
                    <OfferInfoTypeStyled>
                        {currentType}
                    </OfferInfoTypeStyled>
                    <h2 className='mb-0'>{offerInfo.title}</h2>
                </OfferInfoContentTitleContainerStyled>
                <OfferInfoStatusStyled className={classnames({
                    positive: offerInfo.status === PARTNER_OFFERS_STATUS.ACTIVE,
                    negative: [PARTNER_OFFERS_STATUS.CANCELED, PARTNER_OFFERS_STATUS.DECLINED].includes(offerInfo.status),
                })}>
                    <span>{getLangPath(`offers.statuses.${offerInfo.status}`)}</span>
                    <OfferInfoStatusIndicatorStyled className='indicator'/>
                </OfferInfoStatusStyled>
            </OfferInfoContentHeaderStyled>

            <OfferInfoConditionsStyled>
                <table>
                    <tbody>
                    {
                        isTypeCustom && <tr>
                            <th>
                                {getLangPath('offers.custom_conditions')}
                            </th>
                        </tr>
                    }
                    {
                        !!offerInfo.ftd && <tr>
                            <th>{getLangPath('offers.ftd_payout')}</th>
                            <td>{amountFormat(offerInfo.ftd_amount, 0, '$')}</td>
                        </tr>
                    }
                    {
                        !!offerInfo.turnover && <>
                            <tr>
                                <th>{getLangPath('offers.turnover_amount')}</th>
                                <td>{amountFormat(offerInfo.turnover_amount, 0, '$')}</td>
                            </tr>
                            <tr>
                                <th>{getLangPath('offers.turnover_lot')}</th>
                                <td>{formatTurnoverLots(offerInfo.turnover_lot, 0)}</td>
                            </tr>
                        </>
                    }
                    {
                        hasInout && <tr>
                            <th>{getLangPath('offers.inout_payout')}</th>
                            <td>{getLangPath('offers.inout_percent', { percent: offerInfo.inout_percent })}</td>
                        </tr>
                    }
                    {
                        hasRevshare && <tr>
                            <th>{getLangPath('offers.revshare_payout')}</th>
                            <td>
                                {getLangPath('offers.revshare_percent', { percent: offerInfo.revshare_percent })}
                                {!!offerInfo.revshare_carryover && <>
                                    <br/>
                                    {getLangPath('offers.revshare_carryover')}
                                </>}
                            </td>
                        </tr>
                    }
                    {
                        ibOverridePercent > 0 && <tr>
                            <th>{getLangPath('offers.partners_reward')}</th>
                            <td>{ibOverridePercent}%</td>
                        </tr>
                    }
                    </tbody>
                </table>
            </OfferInfoConditionsStyled>

            {
                isOfferActive &&
                <OfferInfoReferralLinkStyled>
                    <OfferReferralLink offerInfo={offerInfo}/>
                </OfferInfoReferralLinkStyled>
            }
        </OfferInfoContentStyled>

        {
            isOfferActive && <>
                <Campaigns offerInfo={offerInfo}/>

                <PartnerPostbacks offerId={offerInfo.id}/>
            </>
        }

        {
            isOfferWaiting && <OfferInfoControlPanelStyled>
                <Button variant="primary" onClick={handleApplyClick}>
                    {getLangPath('offers.apply_btn')}
                </Button>
                <Button variant="outline-danger" onClick={handleDeclineClick}>
                    {getLangPath('offers.decline_btn')}
                </Button>
            </OfferInfoControlPanelStyled>
        }

    </OfferInfoStyled>;
}

export default OfferInfo;
